import React from 'react'

// components
import { Col, Form, Row } from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'

import './Select.css'
import { SelectOption } from './Select'

interface AsyncFormSelectProps {
	name: string
	label?: string
	required?: boolean
	feedback?: string
	isDisabled?: boolean
	isClearable?: boolean
	placeholder?: string | null | number | boolean | JSX.Element
	// The currently selected option (value + label). Async selects can't derive the label
	// from a static options list, so the caller owns the selected option.
	value: SelectOption<string> | null
	onChange: (option: SelectOption<string> | null) => void
	loadOptions: (input: string) => Promise<SelectOption<string>[]>
	// Minimum characters before a search fires (avoids querying on a single keystroke). Default 2.
	minInputLength?: number
	// Debounce delay in ms before loadOptions is called. Default 300.
	debounceMs?: number
	labelCol?: number
	labelJustify?: 'start' | 'end' | 'center' | 'between' | 'around'
}

// Mirrors the Option override in Select.tsx: strips the mouse-over handlers that cause lag on large lists.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomOption = (props: any) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { innerProps, isFocused, ...otherProps } = props
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { onMouseMove, onMouseOver, ...otherInnerProps } = innerProps
	const newProps = { innerProps: { ...otherInnerProps }, ...otherProps }
	return (
		<components.Option {...newProps} className="custom-select-option">
			{props.children}
		</components.Option>
	)
}

const AsyncFormSelect = (props: AsyncFormSelectProps) => {
	const { labelCol, labelJustify, loadOptions, minInputLength = 2, debounceMs = 300, value, onChange, ...htmlProps } = props

	// Debounce the promise-returning loadOptions so we fire at most one request per pause in typing.
	const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const debouncedLoadOptions = React.useCallback(
		(input: string): Promise<SelectOption<string>[]> => {
			if (input.length < minInputLength) {
				return Promise.resolve([])
			}
			return new Promise((resolve) => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
				}
				timeoutRef.current = setTimeout(() => {
					loadOptions(input).then(resolve)
				}, debounceMs)
			})
		},
		[loadOptions, minInputLength, debounceMs]
	)

	const control = (
		<>
			<AsyncSelect
				{...htmlProps}
				value={value}
				onChange={(option) => onChange(option as SelectOption<string> | null)}
				loadOptions={debouncedLoadOptions}
				cacheOptions
				components={{ Option: CustomOption }}
				noOptionsMessage={({ inputValue }) =>
					inputValue.length < minInputLength ? 'Begin typing to search...' : 'No results found'
				}
				styles={{
					singleValue: (base, state) => ({
						// TS def for the base CSS properties is too broad, any cast to stop singleValue complaining
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						...(base as any),
						display: state.selectProps.menuIsOpen ? 'none' : 'block',
					}),
				}}
			/>
			{!!props.feedback && <div className="invalid-feedback-custom">{props.feedback}</div>}
		</>
	)

	return (
		<Form.Group controlId={props.name} as={labelCol ? Row : undefined}>
			{props.label ? (
				<Form.Label
					column={labelCol ? true : undefined}
					sm={labelCol || undefined}
					className={labelJustify ? `d-flex justify-content-${labelJustify}` : undefined}
				>
					{props.label}
					{props.required ? ' *' : ''}
				</Form.Label>
			) : null}
			{labelCol ? <Col sm={12 - labelCol}>{control}</Col> : control}
		</Form.Group>
	)
}

export { AsyncFormSelect }
